import React, { useState, useMemo, useEffect, useRef } from "react";
import type { PrincipalInsuredData } from "../../../types/customer-portal";
import DatePicker from "../../../../../shared/components/ui/DatePicker/DatePicker";
import PremiumUpdateModal from "../../../../../shared/components/ui/PremiumUpdateModal";
import ExistingPolicyModal from "./ExistingPolicyModal";
import SearchableDropdown from "../../../../../components/common/SearchableDropdown";
import { dateUtils } from "../../../../../shared/utils/dateUtils";
import {
  validateIDNumber,
  formatIDNumber,
  getIDFormatInfo,
} from "../../../../../shared/utils/idFormatValidation";
import {
  validateNameField,
  filterNameField,
  validateOccupationField,
  filterOccupationField,
  validateIdNumberField,
  filterIdNumberField,
  getBirthdateFieldError,
  NAME_FIELD_MAX_LENGTH,
  OCCUPATION_MAX_LENGTH,
  ID_NUMBER_MAX_LENGTH,
} from "../../../../../shared/utils/fieldValidation";
import "./PrincipalInsuredStep.css";
import "../../../../../styles/searchable-dropdown.css";

import { useNationalities } from "@/portals/customer/hooks/useNationality";
import { useIDTypes } from "@/portals/customer/hooks/useIDTypes";
import { useUpdatedPremiumPricebyAge } from "@/portals/customer/hooks/useUpdatedPremiumPricebyAge";
import { usePrincipalPolicyValidation } from "@/portals/customer/hooks/usePrincipalPolicyValidation";
import { usePlanSelection } from "@/portals/customer/contexts/PlanSelectionContext";
import { isValid } from "date-fns";
import { useAppStore } from "@/store/useAppStore";

// Field validation highlighting interface
interface FieldValidationErrors {
  [key: string]: boolean;
}

interface PrincipalInsuredStepProps {
  data?: PrincipalInsuredData;
  onNext: (data: PrincipalInsuredData) => void;
  onBack: () => void;
}

const PrincipalInsuredStep: React.FC<PrincipalInsuredStepProps> = ({
  data,
  onNext,
  onBack,
}) => {
  const [formData, setFormData] = useState<PrincipalInsuredData>({
    title: data?.title || "",
    firstName: data?.firstName || "",
    middleName: data?.middleName || "",
    lastName: data?.lastName || "",
    suffix: data?.suffix || "",
    dateOfBirth: data?.dateOfBirth || "",
    gender: data?.gender || "",
    occupation: data?.occupation || "",
    nationality: data?.nationality || "",
    civilStatus: data?.civilStatus || "",
    idType: data?.idType || "",
    idNumber: data?.idNumber || "",
  });

  const clientInformationStepperStateData = sessionStorage.getItem(
    "clientInformationStepperState",
  );
  const parsedClientInformationStepperStateData =
    clientInformationStepperStateData
      ? Object.assign({}, JSON.parse(clientInformationStepperStateData))
      : {};

  // ID number validation state
  const [idNumberError, setIdNumberError] = useState<string | null>(null);
  const [isIdNumberTouched, setIsIdNumberTouched] = useState(false);

  // Field validation highlighting state
  const [fieldErrors, setFieldErrors] = useState<FieldValidationErrors>({});
  const [showValidationErrors, setShowValidationErrors] = useState(false);

  // Use the custom hook to fetch nationalities
  const {
    nationalitiesOptions,
    loading: nationalitiesLoading,
    error: nationalitiesError,
  } = useNationalities();

  // Use the custom hook to fetch valid IDs or ID Types
  const {
    idTypesOptions,
    loading: idTypesLoading,
    error: idTypesError,
  } = useIDTypes();

  // Use the plan selection context
  const {
    selectedPlan,
    setPrincipalInsuredAdjustment,
    isShortSecure,
    isFamilyPlan,
    getTempDataValue,
  } = usePlanSelection();

  const { payment, setAdjustmentPriceByAge } = useAppStore();

  // Get tempData values from context instead of API hook
  const tempDataRateAmount = getTempDataValue("rateAmount") as number | null;
  const tempDataCurrencyCode = getTempDataValue("currencyCode") as
    | string
    | null;
  console.log("PrincipalInsuredStep: tempData values:", {
    rateAmount: tempDataRateAmount,
    currencyCode: tempDataCurrencyCode,
    selectedPlanRateAmount: selectedPlan?.rate?.rateAmount,
    selectedPlanExists: !!selectedPlan,
  });
  // Use the custom hook for premium price calculation by age
  const {
    originalPrice,
    adjustedPrice,
    pabaInfo: _pabaInfo, // eslint-disable-line @typescript-eslint/no-unused-vars
    loading: pabaLoading,
    error: pabaError,
    calculatePremiumByAge,
    clearCalculation: _clearCalculation, // eslint-disable-line @typescript-eslint/no-unused-vars
  } = useUpdatedPremiumPricebyAge();

  // Modal state management
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const [hasShownModalForAge, setHasShownModalForAge] = useState<number | null>(
    null,
  );
  const [showExistingPolicyModal, setShowExistingPolicyModal] = useState(false);
  const [existingPolicyCoverageEnd, setExistingPolicyCoverageEnd] = useState<
    string | null
  >(null);
  const [existingPolicyCoverageStart, setExistingPolicyCoverageStart] =
    useState<string | null>(null);

  // Use the principal policy validation hook
  const { validatePrincipalPolicy, clearState: clearValidationState } =
    usePrincipalPolicyValidation();

  // Ref to track if we've already set the adjustment for the current calculation
  const lastAdjustmentSet = useRef<{
    originalPrice: number;
    adjustedPrice: number;
    age: number;
  } | null>(null);

  // Store premium adjustment in context when calculated (skip for Short Secure and Family plans)
  useEffect(() => {
    if (
      !isShortSecure() &&
      adjustedPrice &&
      originalPrice &&
      adjustedPrice !== originalPrice
    ) {
      const currentAge = formData.dateOfBirth
        ? dateUtils.calculateAge(dateUtils.parseDate(formData.dateOfBirth)!)
        : 0;

      if (currentAge >= 76 && currentAge <= 100) {
        const newAdjustment = {
          originalPrice,
          adjustedPrice,
          age: currentAge,
        };

        // Only update if the adjustment has actually changed
        const lastAdjustment = lastAdjustmentSet.current;
        if (
          !lastAdjustment ||
          lastAdjustment.originalPrice !== originalPrice ||
          lastAdjustment.adjustedPrice !== adjustedPrice ||
          lastAdjustment.age !== currentAge
        ) {
          setPrincipalInsuredAdjustment(newAdjustment);
          lastAdjustmentSet.current = newAdjustment;
        }
      }
    } else {
      // Clear adjustment if no longer applicable or if Short Secure or Family Plan
      if (lastAdjustmentSet.current) {
        setPrincipalInsuredAdjustment(null);
        lastAdjustmentSet.current = null;
      }
    }
  }, [
    adjustedPrice,
    originalPrice,
    formData.dateOfBirth,
    setPrincipalInsuredAdjustment,
    isShortSecure,
    isFamilyPlan,
  ]);

  // Create birthdate validation rules based on coverage type and plan type
  // Individual Plan "Myself": Always uses standard rules (18-100 years) regardless of user status
  // Individual Plan "Someone Else": Uses "Someone Else" rules (18-100 years)
  // Family Plan: Uses Family Plan rules (18-75 years)
  // Short Secure: Uses Short Secure rules (0-75 years) only when appropriate
  const birthdateValidationRules = useMemo(() => {
    if (
      parsedClientInformationStepperStateData?.coverageType === "someone-else"
    ) {
      return dateUtils.createSomeoneElseBirthdateRules();
    }
    if (isFamilyPlan()) {
      return dateUtils.createFamilyPlanBirthdateRules();
    }
    // For Individual Plan "Myself" path, always use standard rules (18-100 years)
    // regardless of whether it's Short Secure or not
    return isShortSecure()
      ? dateUtils.createShortSecureBirthdateRules()
      : dateUtils.createBirthdateRules();
  }, [
    isFamilyPlan,
    isShortSecure,
    parsedClientInformationStepperStateData?.coverageType,
  ]);

  // Inline date of birth error message, derived from the same minDate/maxDate
  // rules the datepicker uses to disable calendar days
  const dateOfBirthError = useMemo(
    () =>
      getBirthdateFieldError(
        formData.dateOfBirth,
        birthdateValidationRules,
      ),
    [formData.dateOfBirth, birthdateValidationRules],
  );

  // Get ID format info for the currently selected ID type
  const idFormatInfo = useMemo(() => {
    return formData.idType ? getIDFormatInfo(formData.idType) : null;
  }, [formData.idType]);

  // Validate ID number whenever idType or idNumber changes
  const idValidationResult = useMemo(() => {
    if (!isIdNumberTouched || !formData.idNumber.trim()) {
      return { isValid: true };
    }
    return validateIDNumber(formData.idType, formData.idNumber);
  }, [formData.idType, formData.idNumber, isIdNumberTouched]);

  // Form validation logic - check all required fields except optional ones
  const isFormValid = useMemo(() => {
    const requiredFields: (keyof PrincipalInsuredData)[] = [
      "title",
      "firstName",
      "lastName",
      "dateOfBirth",
      "gender",
      "occupation",
      "nationality",
      "civilStatus",
      "idType",
      "idNumber",
    ];

    // Check if all required fields are filled
    const allFieldsFilled = requiredFields.every((field) => {
      const value = formData[field];
      return (
        value &&
        value.trim() !== "" &&
        value !== "Choose Gender" &&
        value !== "Nationality" &&
        value !== "Choose Civil Status" &&
        value !== "Choose ID Type"
      );
    });

    // Also check if ID number is valid
    return allFieldsFilled && idValidationResult.isValid;
  }, [formData, idValidationResult]);

  // Get validation errors for required fields
  const getValidationErrors = (): FieldValidationErrors => {
    const errors: FieldValidationErrors = {};

    // Required fields based on task requirements
    if (!formData.title?.trim()) errors.title = true;
    if (!formData.firstName?.trim()) errors.firstName = true;
    if (!formData.lastName?.trim()) errors.lastName = true;
    if (!formData.dateOfBirth?.trim()) errors.dateOfBirth = true;
    if (!formData.gender || formData.gender === "Choose Gender")
      errors.gender = true;
    if (!formData.occupation?.trim()) errors.occupation = true;
    if (!formData.nationality || formData.nationality === "Nationality")
      errors.nationality = true;
    if (!formData.civilStatus || formData.civilStatus === "Choose Civil Status")
      errors.civilStatus = true;
    if (!formData.idType || formData.idType === "Choose ID Type")
      errors.idType = true;
    if (!formData.idNumber?.trim()) errors.idNumber = true;

    // Add pattern validation for name fields
    if (formData.firstName && !validateNameField(formData.firstName))
      errors.firstName = true;
    if (formData.lastName && !validateNameField(formData.lastName))
      errors.lastName = true;
    if (formData.middleName && !validateNameField(formData.middleName))
      errors.middleName = true;

    // Add pattern validation for occupation field
    if (formData.occupation && !validateOccupationField(formData.occupation))
      errors.occupation = true;

    // Add pattern validation for ID number field
    if (formData.idNumber && !validateIdNumberField(formData.idNumber))
      errors.idNumber = true;

    // Also validate ID number format if it's filled (existing validation)
    if (formData.idNumber?.trim() && !idValidationResult.isValid) {
      errors.idNumber = true;
    }

    if (
      getBirthdateFieldError(
        formData.dateOfBirth,
        birthdateValidationRules,
      )
    )
      errors.dateOfBirth = true;

    return errors;
  };

  // Helper function to get field error class
  const getFieldErrorClass = (fieldName: string): string => {
    return showValidationErrors && fieldErrors[fieldName] ? "field-error" : "";
  };

  // Helper function to clear field error when user starts typing
  const clearFieldError = (fieldName: string) => {
    if (fieldErrors[fieldName]) {
      setFieldErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[fieldName];
        return newErrors;
      });
    }
  };

  const handleInputChange = async (
    field: keyof PrincipalInsuredData,
    value: string,
  ) => {
    // Clear field error when user starts typing
    clearFieldError(field);

    let processedValue = value;

    // Apply field-specific filtering and validation
    if (
      field === "firstName" ||
      field === "lastName" ||
      field === "middleName"
    ) {
      processedValue = filterNameField(value);
    } else if (field === "occupation") {
      processedValue = filterOccupationField(value);
    } else if (field === "idNumber") {
      setIsIdNumberTouched(true);
      // Apply ID number filtering first
      processedValue = filterIdNumberField(value);

      if (formData.nationality === "Filipino" || formData.nationality === "") {
        // Format the ID number based on the selected ID type
        if (formData.idType) {
          processedValue = formatIDNumber(formData.idType, processedValue);
        }
      }
    }

    // Clear ID number when ID type changes
    if (field === "idType") {
      setFormData((prev) => ({
        ...prev,
        [field]: processedValue,
        idNumber: "", // Clear ID number when type changes
      }));
      setIsIdNumberTouched(false);
      setIdNumberError(null);
      return;
    }

    if (field === "nationality") {
      setFormData((prev) => ({
        ...prev,
        [field]: processedValue,
        idNumber: "", // Clear ID number when type changed
        idType: "", // clear ID type when nationality changed
      }));

      setIsIdNumberTouched(false);
      setIdNumberError(null);
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [field]: processedValue,
    }));

    // Validate ID number after updating
    if (field === "idNumber" && formData.idType) {
      const validation = validateIDNumber(formData.idType, processedValue);
      setIdNumberError(validation.isValid ? null : validation.message || null);
    }

    // Check for age-based modal trigger when date of birth changes (only for non-Short Secure and non-Family plans)
    if (field === "dateOfBirth" && processedValue && !isShortSecure()) {
      const birthDate = dateUtils.parseDate(processedValue);
      if (birthDate) {
        const age = dateUtils.calculateAge(birthDate);

        // Show modal for ages 76-100, but only once per age
        if (age >= 76 && age <= 100 && hasShownModalForAge !== age) {
          // Get the current premium amount - enhanced priority logic with better validation
          let currentPremiumAmount = 29; // Final fallback

          // Priority 1: selectedPlan rate amount (with proper validation)
          if (
            selectedPlan?.rate?.rateAmount &&
            typeof selectedPlan.rate.rateAmount === "number" &&
            selectedPlan.rate.rateAmount > 0 &&
            !isNaN(selectedPlan.rate.rateAmount)
          ) {
            currentPremiumAmount = selectedPlan.rate.rateAmount;
            console.log(
              "PrincipalInsuredStep: Using selectedPlan rateAmount:",
              currentPremiumAmount,
            );
          }
          // Priority 2: tempData rate amount (with enhanced validation)
          else if (
            tempDataRateAmount !== null &&
            tempDataRateAmount !== undefined &&
            typeof tempDataRateAmount === "number" &&
            tempDataRateAmount > 0 &&
            !isNaN(tempDataRateAmount)
          ) {
            currentPremiumAmount = tempDataRateAmount;
            console.log(
              "PrincipalInsuredStep: Using tempData rateAmount as fallback:",
              currentPremiumAmount,
            );
          }
          // Priority 3: Final fallback
          else {
            console.log(
              "PrincipalInsuredStep: No valid rateAmount found, using final fallback:",
              currentPremiumAmount,
            );
            console.log(
              "PrincipalInsuredStep: Debug - selectedPlan?.rate?.rateAmount:",
              selectedPlan?.rate?.rateAmount,
            );
            console.log(
              "PrincipalInsuredStep: Debug - tempDataRateAmount:",
              tempDataRateAmount,
            );
          }

          // Calculate premium adjustment using the hook
          try {
            await calculatePremiumByAge({
              age: age,
              price: tempDataRateAmount || currentPremiumAmount,
            });
            setShowPremiumModal(true);
            setHasShownModalForAge(age);
          } catch (error) {
            console.error("Failed to calculate premium adjustment:", error);
            // Still show modal even if calculation fails, using static values as fallback
            setShowPremiumModal(true);
            setHasShownModalForAge(age);
          }
        }
      }
    }
  };

  const handleModalClose = () => {
    setShowPremiumModal(false);
  };

  const handleExistingPolicyModalClose = () => {
    setShowExistingPolicyModal(false);
    setExistingPolicyCoverageEnd(null);
    setExistingPolicyCoverageStart(null);
    clearValidationState();
  };

  const handleNext = async () => {
    // Check for validation errors
    const validationErrors = getValidationErrors();
    const hasErrors = Object.keys(validationErrors).length > 0;

    if (hasErrors) {
      // Show validation errors by highlighting fields
      setFieldErrors(validationErrors);
      setShowValidationErrors(true);
      return; // Don't proceed if there are validation errors
    }

    // Clear any previous validation errors
    setFieldErrors({});
    setShowValidationErrors(false);

    // Proceed with form validation
    if (!isFormValid) {
      return;
    }

    try {
      // Validate principal policy for existing coverage
      console.log(
        "🔍 PrincipalInsuredStep: Starting principal policy validation...",
      );
      console.log("🔍 PrincipalInsuredStep: Form data:", formData);

      // Prepare validation data with proper handling of optional fields
      const validationData = {
        firstName: formData.firstName,
        middleName: formData.middleName || "",
        lastName: formData.lastName,
        suffix: formData.suffix || "",
        dateOfBirth: formData.dateOfBirth,
        gender: formData.gender,
        idType: formData.idType,
        idNumber: formData.idNumber,
      };

      console.log(
        "🔍 PrincipalInsuredStep: Validation data prepared:",
        validationData,
      );
      console.log(
        "🔍 PrincipalInsuredStep: About to call validatePrincipalPolicy...",
      );

      const validationResponse = await validatePrincipalPolicy(validationData);

      console.log(
        "🔍 PrincipalInsuredStep: Validation response received:",
        validationResponse,
      );

      // Enhanced modal display logic with better debugging
      console.log(
        "🔍 PrincipalInsuredStep: Checking validation response success value:",
        validationResponse.success,
      );
      console.log(
        "🔍 PrincipalInsuredStep: Response message:",
        validationResponse.message,
      );

      if (!validationResponse.success) {
        // Show existing policy modal if success is false (indicating existing policy)
        console.log(
          "✅ PrincipalInsuredStep: Existing policy detected (success=false), showing modal",
        );
        console.log(
          "✅ PrincipalInsuredStep: Coverage end date:",
          validationResponse.coverage_end,
        );
        console.log("✅ PrincipalInsuredStep: Setting modal state...");

        setExistingPolicyCoverageEnd(validationResponse.coverage_end);
        setExistingPolicyCoverageStart(validationResponse.coverage_start);
        setShowExistingPolicyModal(true);

        console.log(
          "✅ PrincipalInsuredStep: Modal state set, should be visible now",
        );
        console.log(
          "✅ PrincipalInsuredStep: showExistingPolicyModal will be:",
          true,
        );
        console.log(
          "✅ PrincipalInsuredStep: existingPolicyCoverageEnd will be:",
          validationResponse.coverage_end,
        );

        return; // Don't proceed to next step
      }

      if (isValid(new Date(formData.dateOfBirth))) {
        const personInsuredAge = dateUtils.calculateAge(
          new Date(formData.dateOfBirth),
        );

        if (personInsuredAge > 75) {
          // TODO: check if price already adjusted
          if (!payment.adjustedPriceByAge?.rateAmount) {
            // show modal
            await calculatePremiumByAge({
              age: personInsuredAge,
              price: payment.rateAmount,
            });
            setShowPremiumModal(true);
            return;
          }
        } else {
          setAdjustmentPriceByAge(payment.rateAmount);
        }
      }

      // If success is true, proceed to next step
      console.log(
        "➡️ PrincipalInsuredStep: No existing policy (success=true), proceeding to next step",
      );
      onNext(formData);
    } catch (error) {
      console.error(
        "PrincipalInsuredStep: Error during policy validation:",
        error,
      );
      // If validation fails due to error, still proceed to next step
      // This ensures the user flow isn't blocked by API issues
      onNext(formData);
    }
  };

  return (
    <>
      <div className="principal-insured-step">
        <div className="step-header">
          <h3 className="step-title">Principal Insured</h3>
          <p className="step-description">
            Please provide the details of the Main Policyholder to be covered by
            this travel insurance.
          </p>
        </div>

        <div className="step-form">
          {/* Name Fields Row */}
          <div className="form-row name-row">
            <div className={`form-group title-group ${getFieldErrorClass("title")}`}>
              <label className="form-label">Title *</label>
              <select
                className="form-select"
                value={formData.title}
                onChange={(e) => handleInputChange("title", e.target.value)}
              >
                <option value="">Title</option>
                <option value="Ms.">Ms.</option>
                <option value="Mrs.">Mrs.</option>
                <option value="Mr.">Mr.</option>
              </select>
            </div>

            <div className={`form-group ${getFieldErrorClass("firstName")}`}>
              <label className="form-label">First Name *</label>
              <input
                type="text"
                className="form-input"
                value={formData.firstName}
                onChange={(e) => handleInputChange("firstName", e.target.value)}
                placeholder=""
                maxLength={NAME_FIELD_MAX_LENGTH}
              />
            </div>

            <div className="form-group">
              <label className="form-label">Middle Name</label>
              <input
                type="text"
                className="form-input"
                value={formData.middleName}
                onChange={(e) =>
                  handleInputChange("middleName", e.target.value)
                }
                placeholder=""
                maxLength={NAME_FIELD_MAX_LENGTH}
              />
            </div>

            <div className={`form-group ${getFieldErrorClass("lastName")}`}>
              <label className="form-label">Last Name *</label>
              <input
                type="text"
                className="form-input"
                value={formData.lastName}
                onChange={(e) => handleInputChange("lastName", e.target.value)}
                placeholder=""
                maxLength={NAME_FIELD_MAX_LENGTH}
              />
            </div>

            <div className="form-group suffix-group">
              <label className="form-label">Suffix</label>
              <select
                className="form-select"
                value={formData.suffix}
                onChange={(e) => handleInputChange("suffix", e.target.value)}
              >
                <option value="">N/A</option>
                <option value="Jr.">Jr.</option>
                <option value="Sr.">Sr.</option>
                <option value="II">II</option>
                <option value="III">III</option>
              </select>
            </div>
          </div>

          {/* Second Row */}
          <div className="form-row">
            <div className={`form-group ${getFieldErrorClass("dateOfBirth")}`}>
              <label className="form-label">Date of Birth *</label>
              <DatePicker
                value={formData.dateOfBirth}
                onChange={(value) => handleInputChange("dateOfBirth", value)}
                placeholder="MM/DD/YYYY"
                name="dateOfBirth"
                className="form-datepicker principal-insured-step-date-picker-wrapper"
                position="above"
                validationRules={birthdateValidationRules}
                error={
                  showValidationErrors && fieldErrors.dateOfBirth
                    ? dateOfBirthError ?? undefined
                    : undefined
                }
              />
            </div>

            <div className={`form-group ${getFieldErrorClass("gender")}`}>
              <label className="form-label">Gender *</label>
              <select
                className="form-select"
                value={formData.gender}
                onChange={(e) => handleInputChange("gender", e.target.value)}
              >
                <option value="">Choose Gender</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
              </select>
            </div>

            <div className={`form-group ${getFieldErrorClass("occupation")}`}>
              <label className="form-label">Occupation *</label>
              <input
                type="text"
                className="form-input"
                value={formData.occupation}
                onChange={(e) =>
                  handleInputChange("occupation", e.target.value)
                }
                placeholder=""
                maxLength={OCCUPATION_MAX_LENGTH}
              />
            </div>

            <div className={`form-group ${getFieldErrorClass("nationality")}`}>
              <label className="form-label">Nationality *</label>
              <SearchableDropdown
                options={nationalitiesOptions}
                selectedValue={formData.nationality}
                onChange={(value) => handleInputChange("nationality", value)}
                placeholder={
                  nationalitiesLoading
                    ? "Loading nationalities..."
                    : "Choose Nationality"
                }
                searchPlaceholder="Search nationalities..."
                disabled={nationalitiesLoading || !!nationalitiesError}
                dropdownId="nationality-dropdown"
              />
            </div>
          </div>

          {/* Third Row */}
          <div className="form-row">
            <div className={`form-group ${getFieldErrorClass("civilStatus")}`}>
              <label className="form-label">Civil Status *</label>
              <select
                className="form-select"
                value={formData.civilStatus}
                onChange={(e) =>
                  handleInputChange("civilStatus", e.target.value)
                }
              >
                <option value="">Choose Civil Status</option>
                <option value="Single">Single</option>
                <option value="Married">Married</option>
                <option value="Separated/Divorced">Separated/Divorced</option>
                <option value="Widow/Widower">Widow/Widower</option>
              </select>
            </div>

            <div className={`form-group ${getFieldErrorClass("idType")}`}>
              <label className="form-label">ID Type *</label>
              <SearchableDropdown
                options={idTypesOptions
                  .filter((idType) => {
                    return formData.nationality === "Filipino" ||
                      formData.nationality === ""
                      ? idType.isNonFilipino === false
                      : idType.isNonFilipino === true;
                  })
                  .map((idType) => idType.name)}
                selectedValue={formData.idType}
                onChange={(value) => handleInputChange("idType", value)}
                placeholder={
                  idTypesLoading ? "Loading ID Types..." : "Choose ID Type"
                }
                searchPlaceholder="Search ID types..."
                disabled={idTypesLoading || !!idTypesError}
                dropdownId="id-type-dropdown"
              />
            </div>

            <div
              className={`form-group id-number-group ${getFieldErrorClass("idNumber")}`}
            >
              <label className="form-label">ID Number *</label>
              <div className="id-input-container">
                <input
                  type="text"
                  className={`form-input ${idNumberError ? "error" : ""} ${formData.idNumber && idValidationResult.isValid ? "valid" : ""}`}
                  value={formData.idNumber}
                  onChange={(e) =>
                    handleInputChange("idNumber", e.target.value)
                  }
                  placeholder={idFormatInfo?.example || ""}
                  maxLength={ID_NUMBER_MAX_LENGTH}
                />
              </div>
            </div>
          </div>

          {/* Error messages */}
          {nationalitiesError && (
            <div className="error-message">
              <p>
                Unable to load nationalities. Please refresh the page or try
                again later.
              </p>
            </div>
          )}
          {idTypesError && (
            <div className="error-message">
              <p>
                Unable to load ID types. Please refresh the page or try again
                later.
              </p>
            </div>
          )}
          {pabaError && (
            <div className="error-message">
              <p>Unable to calculate premium adjustment: {pabaError}</p>
            </div>
          )}
        </div>
      </div>
      {/* Navigation Buttons */}
      <div className="step-navigation">
        <button type="button" className="btn-back" onClick={onBack}>
          Back
        </button>
        <button type="button" className="btn-next" onClick={handleNext}>
          Next
        </button>
      </div>

      {/* Premium Update Modal */}
      <PremiumUpdateModal
        isOpen={showPremiumModal}
        onClose={handleModalClose}
        adjustedPrice={adjustedPrice}
        pabaLoading={pabaLoading}
      />

      {/* Existing Policy Modal */}
      {console.log(
        "🔍 PrincipalInsuredStep: Rendering ExistingPolicyModal with props:",
        {
          isOpen: showExistingPolicyModal,
          coverageEndDate: existingPolicyCoverageEnd,
        },
      )}
      <ExistingPolicyModal
        isOpen={showExistingPolicyModal}
        onClose={handleExistingPolicyModalClose}
        coverageEndDate={existingPolicyCoverageEnd}
        coverageStartDate={existingPolicyCoverageStart}
      />
    </>
  );
};

export default PrincipalInsuredStep;
